import reactUpdate from 'immutability-helper';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import type {ReactionConditionsStructuredTable} from 'notebook/integrations/chemistry/ReactionConditionsStructuredTableAdapter';
import {updateStoichiometryCalculationSettingsActionFunction} from 'notebook/integrations/chemistry/StoichiometryCalculationSettingsModal';
import {
  downloadStoichiometryCSVActionFunction,
  exportStoichiometryAuditLogsActionFunction,
} from 'notebook/integrations/chemistry/stoichiometryGroupActionFunctions';
import {StoichiometryStructuredTable} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import submitReactionGroupActionFunction from 'notebook/notes/groups/action-functions/submitReactionGroupActionFunction';
import GenericNoteLinkedGroupAdapter from 'notebook/notes/groups/GenericNoteLinkedGroupAdapter';
import {
  CreateNoteLinkedGroupProps,
  GroupActionFunctions,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {STRUCTURED_TABLE_PLUGIN} from 'notebook/structured-tables/StructuredTablePlugin';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {isNoteLinkedFile, isStructuredTable} from 'notebook/TypePredicates';
import {
  NoteLinkedFile,
  NoteLinkedObjectType,
  NotePart,
  NotePartWithItem,
  isNotePartWithItem,
} from 'notebook/util/NoteUtilConstants';
import {Schema} from 'schemas/SchemaConstants';
import uniqueId from 'util/uniqueId';

import type {
  StoichiometryCreateGroupExtras,
  StoichiometryGroup,
  StoichiometryGroupMetadata,
} from './StoichiometryTypes';

/**
 * A default empty NoteLinkedFile used for rendering the reaction scheme sketcher
 * when no NoteLinkedFile part exists yet in the group (e.g., legacy groups or
 * newly created groups before a reaction scheme is drawn).
 */
export function makeEmptyReactionSchemeFile(name: string): NoteLinkedFile {
  return {
    cid: uniqueId('nlf'),
    type: NoteLinkedObjectType.NOTE_LINKED_FILE,
    name,
    tfiFileMeta: null,
    externalFileExcerpt: null,
    excerptType: null,
    externalFile: null,
    previewFile: null,
    sourcePreviewFile: null,
    missingPreviewWarning: null,
    isPreviewTransformed: null,
  };
}

/**
 * Wraps a created StructuredTable in a NoteLinkedObject note part for inclusion in a group.
 */
function makeStructuredTableNotePart(structuredTable: object): Omit<NotePart, 'cid'> {
  // @ts-expect-error(2322): This is passing in `shouldGenerateCid: false` but we are expecting it to have a cid
  const tablePart: Omit<NotePart, 'cid'> = reactUpdate(
    makeDefaultItem(ModelTypeConstants.NOTE_LINKED_OBJECT, null, {shouldGenerateCid: false}),
    {
      // @ts-expect-error(2322): The NotePart StructuredTable mapping uses unknown instead of a type parameter
      item: {$set: structuredTable},
    }
  );
  return tablePart;
}

export type {StoichiometryCreateGroupExtras, StoichiometryGroup, StoichiometryGroupMetadata};

/**
 * Maps each StructuredTableKind that can appear inside a Stoichiometry group to its concrete
 * StructuredTable type, so {@link getContainedStructuredTableNotePart} returns a precisely-typed part.
 */
interface TableKindToStructuredTableType {
  [StructuredTableKind.STOICHIOMETRY]: StoichiometryStructuredTable;
  [StructuredTableKind.REACTION_CONDITIONS]: ReactionConditionsStructuredTable;
}

export class StoichiometryNoteLinkedGroupAdapter extends GenericNoteLinkedGroupAdapter<
  StoichiometryGroupMetadata,
  StoichiometryCreateGroupExtras
> {
  getDisplayName(_group: StoichiometryGroup): string {
    return 'Stoichiometry';
  }

  override getActionFunctions(): GroupActionFunctions<
    StoichiometryGroupMetadata,
    StoichiometryCreateGroupExtras
  > {
    const inherited = super.getActionFunctions();
    return {
      ...inherited,
      primary: submitReactionGroupActionFunction,
      secondary: [updateStoichiometryCalculationSettingsActionFunction, ...(inherited.secondary ?? [])],
      overflow: [
        ...(inherited.overflow ?? []),
        exportStoichiometryAuditLogsActionFunction,
        downloadStoichiometryCSVActionFunction,
      ],
      footer: inherited.footer ?? [],
    };
  }

  createNoteParts(
    createProps: CreateNoteLinkedGroupProps<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>
  ): Array<Omit<NotePart, 'cid'>> {
    assert(
      createProps.kind === this.kind,
      `Stoichiometry adapter only supports ${this.kind}, got ${createProps.kind}`
    );
    const {name, entryId, selectorsById, isConfig, extras} = createProps;
    const structuredTableInsertionContext =
      extras.structuredTableInsertionContext ?? EMPTY_PLACEHOLDER_INSERTION_CONTEXT;
    const stoichiometryTable = STRUCTURED_TABLE_PLUGIN.get(StructuredTableKind.STOICHIOMETRY).createTable({
      name,
      schemas: [],
      entryId,
      metadata: {},
      selectorsById,
      isConfig,
      structuredTableInsertionContext,
    });

    const noteParts: Array<Omit<NotePart, 'cid'>> = [makeStructuredTableNotePart(stoichiometryTable)];

    // The Reaction Conditions sub-table is only created behind REACTION_PARAMETERS_ENABLED. With the
    // flag off, the group contains only the stoichiometry table.
    if (BenchlingConfig.REACTION_PARAMETERS_ENABLED) {
      const reactionConditionsTable = STRUCTURED_TABLE_PLUGIN.get(
        StructuredTableKind.REACTION_CONDITIONS
      ).createTable({
        // A distinct, entry-unique table name keeps this table from overwriting another in the
        // per-name evaluation map.
        name: extras.getNextUniqueName(`${name} - Reaction Conditions`, {allowNoSuffix: true}),
        schemas: [],
        entryId,
        metadata: {},
        selectorsById,
        isConfig,
        structuredTableInsertionContext,
      });
      noteParts.push(makeStructuredTableNotePart(reactionConditionsTable));
    }

    return noteParts;
  }

  getReactionSchemeNotePart(group: StoichiometryGroup): NotePartWithItem<NoteLinkedFile> | null {
    const notePart = group.containedNoteParts.find(
      (part) => isNotePartWithItem(part) && isNoteLinkedFile(part.item)
    );
    return (notePart as NotePartWithItem<NoteLinkedFile>) ?? null;
  }

  /**
   * Finds the contained note part holding the StructuredTable of the given kind, typed to that
   * kind's concrete StructuredTable via {@link TableKindToStructuredTableType}. Returns null if the
   * group has no such table.
   */
  private getContainedStructuredTableNotePart<K extends keyof TableKindToStructuredTableType>(
    group: StoichiometryGroup,
    tableKind: K
  ): NotePartWithItem<TableKindToStructuredTableType[K]> | null {
    const notePart = group.containedNoteParts.find(
      (part) => isNotePartWithItem(part) && isStructuredTable(part.item) && part.item.kind === tableKind
    );
    return (notePart as NotePartWithItem<TableKindToStructuredTableType[K]>) ?? null;
  }

  getStoichiometryTableNotePart(group: StoichiometryGroup): NotePartWithItem<StoichiometryStructuredTable> {
    const notePart = this.getContainedStructuredTableNotePart(group, StructuredTableKind.STOICHIOMETRY);
    assert(notePart != null, 'Stoichiometry group must contain stoichiometry StructuredTable');
    return notePart;
  }

  getReactionConditionsTableNotePart(
    group: StoichiometryGroup
  ): NotePartWithItem<ReactionConditionsStructuredTable> | null {
    // Currently only present if added while REACTION_PARAMETERS_ENABLED enabled so can be null.
    return this.getContainedStructuredTableNotePart(group, StructuredTableKind.REACTION_CONDITIONS);
  }

  override supportsCopyPaste(): boolean {
    return true;
  }

  override supportsCrossEntryPaste(): boolean {
    return true;
  }

  override requiresTableEvaluationOnInsert(): boolean {
    return true;
  }

  override supportsEntryLevelEvaluation(): boolean {
    return true;
  }

  getGroupSchemas(_group: StoichiometryGroup): Array<Schema> {
    return [];
  }

  getReactionStructuredTable(group: StoichiometryGroup): StoichiometryStructuredTable {
    return this.getStoichiometryTableNotePart(group).item;
  }

  getReactionConditionsTable(group: StoichiometryGroup): ReactionConditionsStructuredTable | null {
    return this.getReactionConditionsTableNotePart(group)?.item ?? null;
  }
}
